import { supabase } from '../supabase';
import { Database } from '../../types/database';

type Payment = Database['public']['Tables']['payments']['Row'];
type PaymentInsert = Database['public']['Tables']['payments']['Insert'];

export const paymentService = {
  /**
   * Get all payments with invoice details
   */
  getPayments: async () => {
    const { data, error } = await supabase
      .from('payments')
      .select(`
        *,
        invoice:invoices(
          invoice_number,
          total,
          client:clients(name, company)
        )
      `)
      .order('payment_date', { ascending: false });
    return { data, error };
  },

  /**
   * Get payments by invoice ID
   */
  getPaymentsByInvoice: async (invoiceId: string) => {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('payment_date', { ascending: false });
    return { data, error };
  },

  /**
   * Create a new payment
   */
  createPayment: async (payment: Omit<PaymentInsert, 'id' | 'created_at'>) => {
    const { data, error } = await supabase
      .from('payments')
      .insert(payment)
      .select()
      .single();
    return { data, error };
  },

  /**
   * Get payment by ID
   */
  getPaymentById: async (id: string) => {
    const { data, error } = await supabase
      .from('payments')
      .select(`
        *,
        invoice:invoices(
          invoice_number,
          total,
          client:clients(name, company)
        )
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  /**
   * Get total payments for an invoice
   */
  getTotalPaymentsForInvoice: async (invoiceId: string) => {
    const { data, error } = await supabase
      .from('payments')
      .select('amount')
      .eq('invoice_id', invoiceId);
    
    if (error) return { total: 0, error };
    
    const total = data.reduce((sum, payment) => sum + Number(payment.amount), 0);
    return { total, error: null };
  },

  /**
   * Check if invoice is fully paid
   */
  isInvoiceFullyPaid: async (invoiceId: string) => {
    // Get invoice total
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('total')
      .eq('id', invoiceId)
      .single();

    if (invoiceError || !invoice) {
      return { isPaid: false, error: invoiceError };
    }

    // Get total payments
    const { total: totalPaid, error: paymentsError } = await paymentService.getTotalPaymentsForInvoice(invoiceId);

    if (paymentsError) {
      return { isPaid: false, error: paymentsError };
    }

    const isPaid = totalPaid >= Number(invoice.total);
    return { isPaid, error: null };
  }
};